<script setup lang="ts">
import { computed, ref, watch } from "vue";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  vatId?: string;
  errors?: Record<string, string>;
  loading?: boolean;
  submit: (vatId: string) => void;
}

const props = withDefaults(defineProps<Props>(), {
  vatId: "",
  errors: () => ({}),
  loading: false,
});

//
// Data
const localVatId = ref<string>(props.vatId);

//
// Computed
const vatIdError = computed<string>(() => props.errors?.vat_id ?? "");

//
// Watchers
watch(() => props.vatId, (vatId: string) => {
  localVatId.value = vatId ?? "";
});

//
// Exposed Test Vars
defineExpose({ localVatId, vatIdError });
</script>

<template>
  <form
    class="billing-vat-id"
    @submit.prevent="submit(localVatId)"
  >
    <div class="alert alert-info billing-vat-id__description">
      {{ __("settings_billing_page.vat_id.description") }}
    </div>

    <div class="form-group">
      <label
        class="billing-vat-id__label"
        for="billing-vat-id"
      >
        {{ __("settings_billing_page.vat_id.label") }}
      </label>
      <input
        id="billing-vat-id"
        v-model="localVatId"
        type="text"
        name="vat_id"
        class="form-control billing-vat-id__value _vat_id"
        :class="{ 'is-invalid': vatIdError !== '' }"
      >
      <span
        v-if="vatIdError !== ''"
        class="invalid-feedback billing-vat-id__error _vat_error"
        role="alert"
      >
        {{ vatIdError }}
      </span>
    </div>

    <div class="form-group text-right mb-0">
      <button
        type="submit"
        class="btn btn-teal billing-vat-id__submit _vat_submit"
        :disabled="loading"
      >
        {{ __("settings_billing_page.vat_id.update") }}
        <i
          v-if="loading"
          class="fa fa-spin fa-spinner ml-1"
        />
      </button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.billing-vat-id {
  &__error {
    display: block;
  }
}
</style>
